<script>
  // Панель общего списка чата (этап 8.3) — по образцу путевой панели.
  //
  // Правят все участники: тап по пункту отмечает, поле снизу добавляет, long-press
  // открывает «Удалить». Правки оптимистичные: применяем сразу, при ошибке API
  // возвращаем прежний снимок и показываем текст ошибки. Каждый ответ сервера —
  // ПОЛНЫЙ снимок списка, он же приходит событием 'list' от других участников,
  // поэтому мержить дельты не нужно.
  import { createEventDispatcher, onMount } from 'svelte';
  import ActionSheet from './ActionSheet.svelte';
  import { longpress } from '../actions/longpress.js';
  import { api } from '../api.js';
  import { TEXT } from '../ui-text.js';
  import { createArm } from '../arm.js';

  // В13 (1.27.8): «Очистить отмеченные» — вторым касанием: пункты уходят насовсем.
  const { armed: clearArmed, tap: tapClear } = createArm();

  export let chatId = null;
  // Снимок с сервера: { chatType, items, total, done }.
  export let snapshot = null;
  // Чат в персональном архиве участника — список только для чтения.
  export let readOnly = false;
  // map userId -> {displayName, avatarDataUrl} для мини-аватаров в общем чате.
  export let roster = {};
  export let isShared = false;

  const dispatch = createEventDispatcher();
  const COLLAPSED_KEY = 'petrovich_list_panel_collapsed';

  let items = [];
  let collapsed = false;
  let newItemText = '';
  let errorText = '';
  let busyItems = {};
  let menuItem = null;
  let clearing = false;
  let highlightedId = null;
  let highlightTimer = null;

  $: items = Array.isArray(snapshot?.items) ? snapshot.items : [];
  $: doneCount = items.filter((item) => item.isDone).length;
  $: summary = TEXT.list.summary
    .replace('{done}', String(doneCount))
    .replace('{total}', String(items.length));
  $: itemActions = [
    { id: 'remove', label: TEXT.list.removeItem, icon: 'trash', danger: true },
  ];

  onMount(() => {
    try {
      collapsed = window.localStorage.getItem(COLLAPSED_KEY) === '1';
    } catch {
      collapsed = false;
    }
    const onListEvent = (event) => applyRemoteEvent(event.detail);
    window.addEventListener('mqtt:event', onListEvent);
    return () => {
      window.removeEventListener('mqtt:event', onListEvent);
      if (highlightTimer) {
        window.clearTimeout(highlightTimer);
      }
    };
  });

  // Снимок от другого участника (или от Петровича) — заменяем целиком.
  function applyRemoteEvent(payload) {
    if (!payload || payload.type !== 'list' || payload.chatId !== chatId || !payload.list) {
      return;
    }
    const previousById = new Map(items.map((item) => [item.itemId, item]));
    const incoming = Array.isArray(payload.list.items) ? payload.list.items : [];
    const changed = incoming.find((item) => {
      const previous = previousById.get(item.itemId);
      return !previous || previous.isDone !== item.isDone || previous.text !== item.text;
    });
    publish(payload.list);
    if (changed) {
      highlight(changed.itemId);
    }
  }

  function highlight(itemId) {
    highlightedId = itemId;
    if (highlightTimer) {
      window.clearTimeout(highlightTimer);
    }
    highlightTimer = window.setTimeout(() => {
      highlightedId = null;
    }, 2000);
  }

  // Единственная точка правки снимка: наружу уходит то же, что показываем.
  function publish(next) {
    snapshot = next;
    dispatch('change', next);
  }

  function toggleCollapsed() {
    collapsed = !collapsed;
    try {
      window.localStorage.setItem(COLLAPSED_KEY, collapsed ? '1' : '0');
    } catch {
      /* приватный режим — состояние живёт только в этой сессии */
    }
  }

  function displayName(userId) {
    if (!userId) {
      return '';
    }
    if (userId === 'assistant') {
      return TEXT.family.assistantName;
    }
    return roster[userId]?.displayName || userId;
  }

  function avatarOf(userId) {
    return userId === 'assistant' ? null : roster[userId]?.avatarDataUrl || null;
  }

  function authorTitle(item) {
    if (item.addedBy === 'assistant') {
      return TEXT.list.addedByAssistant;
    }
    return TEXT.list.addedBy.replace('{name}', displayName(item.addedBy));
  }

  function doneTitle(item) {
    return TEXT.list.doneBy.replace('{name}', displayName(item.doneBy));
  }

  async function toggleItem(item) {
    if (readOnly || busyItems[item.itemId]) {
      return;
    }
    const previous = snapshot;
    const optimistic = items.map((current) =>
      current.itemId === item.itemId ? { ...current, isDone: !current.isDone } : current
    );
    busyItems = { ...busyItems, [item.itemId]: true };
    errorText = '';
    publish({ ...snapshot, items: optimistic });
    try {
      publish(await api.updateChatListItem(chatId, item.itemId, { isDone: !item.isDone }));
    } catch (error) {
      console.error('Failed to toggle list item', error);
      publish(previous);
      errorText = TEXT.list.updateFailed;
    } finally {
      const { [item.itemId]: _dropped, ...rest } = busyItems;
      busyItems = rest;
    }
  }

  async function addItem() {
    const text = newItemText.trim();
    if (readOnly || !text || !chatId) {
      return;
    }
    newItemText = '';
    errorText = '';
    try {
      publish(await api.addChatListItem(chatId, text));
    } catch (error) {
      console.error('Failed to add list item', error);
      newItemText = text;
      errorText = TEXT.list.addFailed;
    }
  }

  function handleAddKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      addItem();
    }
  }

  function openItemMenu(item) {
    if (readOnly) {
      return;
    }
    menuItem = item;
  }

  async function handleItemAction(event) {
    const item = menuItem;
    menuItem = null;
    if (!item || event.detail.id !== 'remove') {
      return;
    }
    errorText = '';
    try {
      publish(await api.deleteChatListItem(chatId, item.itemId));
    } catch (error) {
      console.error('Failed to delete list item', error);
      errorText = TEXT.list.removeFailed;
    }
  }

  async function clearDone() {
    if (readOnly || clearing) {
      return;
    }
    clearing = true;
    errorText = '';
    try {
      publish(await api.clearDoneChatListItems(chatId));
    } catch (error) {
      console.error('Failed to clear done list items', error);
      errorText = TEXT.list.clearFailed;
    } finally {
      clearing = false;
    }
  }
</script>

<section class="list-panel" aria-label={TEXT.list.panelTitle}>
  <div class="list-heading">
    <button
      type="button"
      class="list-toggle"
      aria-expanded={!collapsed}
      aria-controls="chat-list-items"
      aria-label={collapsed ? TEXT.list.expand : TEXT.list.collapse}
      on:click={toggleCollapsed}
    >
      <span class="list-title">{TEXT.list.panelTitle}</span>
      <span class="list-summary">{summary}</span>
      <span class="list-chevron" class:collapsed aria-hidden="true">⌄</span>
    </button>
  </div>

  {#if !collapsed}
    <div id="chat-list-items" class="list-body">
      {#if items.length}
        <ul class="list-items">
          {#each items as item (item.itemId)}
            <li
              class="list-item"
              class:done={item.isDone}
              class:highlight={highlightedId === item.itemId}
              use:longpress
              on:longpress={() => openItemMenu(item)}
            >
              <button
                type="button"
                class="item-check"
                role="checkbox"
                aria-checked={item.isDone}
                disabled={readOnly}
                on:click={() => toggleItem(item)}
              >
                <span class="check-box" aria-hidden="true">{item.isDone ? '✓' : ''}</span>
                <span class="item-text">{item.text}</span>
              </button>
              {#if isShared}
                <span class="item-author" title={item.isDone ? doneTitle(item) : authorTitle(item)}>
                  {#if item.isDone && item.doneBy}
                    {#if avatarOf(item.doneBy)}
                      <img class="author-mini" src={avatarOf(item.doneBy)} alt="" />
                    {:else}
                      <span class="author-mini author-fallback">{displayName(item.doneBy).slice(0, 1).toUpperCase()}</span>
                    {/if}
                  {:else if item.addedBy === 'assistant'}
                    <span class="author-mini author-assistant" aria-label={TEXT.list.addedByAssistant}>П</span>
                  {:else if avatarOf(item.addedBy)}
                    <img class="author-mini" src={avatarOf(item.addedBy)} alt="" />
                  {:else}
                    <span class="author-mini author-fallback">{displayName(item.addedBy).slice(0, 1).toUpperCase()}</span>
                  {/if}
                </span>
              {/if}
            </li>
          {/each}
        </ul>
      {:else}
        <p class="list-empty">{TEXT.list.empty}</p>
      {/if}

      {#if errorText}
        <div class="list-error" role="alert">{errorText}</div>
      {/if}

      {#if readOnly}
        <p class="list-readonly">{TEXT.list.readOnly}</p>
      {:else}
        <div class="list-add">
          <input
            type="text"
            maxlength="200"
            placeholder={TEXT.list.addPlaceholder}
            aria-label={TEXT.list.addPlaceholder}
            bind:value={newItemText}
            on:keydown={handleAddKeydown}
          />
          <button type="button" class="add-btn" disabled={!newItemText.trim()} on:click={addItem}>
            {TEXT.list.addSubmit}
          </button>
        </div>
        {#if doneCount > 0}
          <button
            type="button"
            class="clear-btn"
            class:armed={$clearArmed === 'clear-done'}
            disabled={clearing}
            on:click={() => tapClear('clear-done') && clearDone()}
          >
            {$clearArmed === 'clear-done' ? TEXT.list.clearDoneConfirm : TEXT.list.clearDone}
          </button>
        {/if}
      {/if}
    </div>
  {/if}
</section>

<ActionSheet
  open={Boolean(menuItem)}
  title={menuItem?.text || TEXT.list.itemActions}
  actions={itemActions}
  on:select={handleItemAction}
  on:close={() => (menuItem = null)}
/>

<style>
  .clear-btn.armed {
    color: var(--error-text);
    font-weight: 600;
  }

  .list-panel {
    background: var(--surface, #1b1b1f);
    border-bottom: 1px solid var(--border, #2c2c33);
    flex-shrink: 0;
  }

  .list-heading {
    display: flex;
  }

  .list-toggle {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    background: none;
    border: none;
    color: var(--text, #e8e8ea);
    font-size: 0.9375rem;
    text-align: left;
    cursor: pointer;
    min-height: 44px;
  }

  .list-title {
    font-weight: 600;
  }

  .list-summary {
    color: var(--text-muted, #9a9aa2);
    font-size: 0.8125rem;
  }

  .list-chevron {
    margin-left: auto;
    font-size: 1.125rem;
    line-height: 1;
    color: var(--text-muted, #9a9aa2);
    transition: transform 0.15s ease;
  }

  .list-chevron.collapsed {
    transform: rotate(-90deg);
  }

  .list-body {
    padding: 0 14px 12px;
    /* Список главнее беседы, но ленту не съедает: длинный список прокручивается внутри. */
    max-height: 40vh;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  .list-items {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .list-item {
    display: flex;
    align-items: center;
    gap: 8px;
    border-bottom: 1px solid var(--border-subtle, #26262c);
  }

  .list-item.highlight {
    background: var(--accent-soft, rgba(90, 140, 255, 0.14));
    border-radius: 8px;
  }

  .item-check {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 2px;
    min-height: 44px;
    background: none;
    border: none;
    color: inherit;
    font-size: 0.9375rem;
    text-align: left;
    cursor: pointer;
  }

  .item-check:disabled {
    cursor: default;
    opacity: 0.7;
  }

  .check-box {
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    border: 2px solid var(--border-strong, #4a4a54);
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.875rem;
    line-height: 1;
    color: var(--accent, #5a8cff);
  }

  .list-item.done .check-box {
    border-color: var(--accent, #5a8cff);
  }

  .list-item.done .item-text {
    text-decoration: line-through;
    color: var(--text-muted, #9a9aa2);
  }

  .item-text {
    word-break: break-word;
  }

  .item-author {
    flex-shrink: 0;
  }

  .author-mini {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    object-fit: cover;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.6875rem;
    font-weight: 600;
  }

  .author-fallback {
    background: var(--surface-alt, #2c2c33);
    color: var(--text-muted, #9a9aa2);
  }

  .author-assistant {
    background: var(--accent, #5a8cff);
    color: #fff;
  }

  .list-empty,
  .list-readonly {
    margin: 10px 0 0;
    color: var(--text-muted, #9a9aa2);
    font-size: 0.875rem;
  }

  .list-error {
    margin-top: 8px;
    color: var(--danger, #ff6b6b);
    font-size: 0.875rem;
  }

  .list-add {
    display: flex;
    gap: 8px;
    margin-top: 10px;
  }

  .list-add input {
    flex: 1;
    min-height: 44px;
    padding: 8px 12px;
    border: 1px solid var(--border, #2c2c33);
    border-radius: 10px;
    background: var(--surface-alt, #232329);
    color: var(--text, #e8e8ea);
    /* max(16px, 1rem): поле <16px заставляет iOS зумить страницу при фокусе. */
    font-size: max(16px, 1rem);
  }

  .add-btn,
  .clear-btn {
    min-height: 44px;
    padding: 8px 14px;
    border: none;
    border-radius: 10px;
    background: var(--accent, #5a8cff);
    color: #fff;
    font-size: 0.9375rem;
    cursor: pointer;
  }

  .clear-btn {
    margin-top: 8px;
    background: none;
    color: var(--text-muted, #9a9aa2);
    padding: 8px 2px;
  }

  .add-btn:disabled,
  .clear-btn:disabled {
    opacity: 0.5;
    cursor: default;
  }
</style>
